import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeCard,
  WireframeButton,
  CrisisBadge,
} from './WireframeLayout';
import { FileText, Zap, ChevronDown, Minus, Plus, Play, SkipBack, SkipForward, HelpCircle, Globe, MapPin, Database, Activity, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { TutorialStartButton } from './WireframeTutorial';
import {
  nrcLocations,
  nrcStatistics,
  regionColors,
  getOperationalLocations,
  NRCRegion,
  NRCLocation
} from '../../data/nrcLocations';
import { mockEWASSources, mockEWASStatistics } from '../../data/ewasData';

interface CrisisData {
  id: string;
  name: string;
  coordinates: [number, number];
  severity: 'critical' | 'high' | 'medium' | 'response';
  cases?: number;
  trend?: string;
  region?: string;
  status?: string;
  members?: number;
  affectedPopulation?: number;
}

// Country configuration with center, zoom, and regions
// Priority countries with detailed crisis data
const priorityCountryConfig: Record<string, { center: [number, number]; zoom: number; regions: string[]; name: string; hasCrisisData: boolean }> = {
  'Sudan': {
    center: [30.0, 15.5],
    zoom: 5,
    name: 'Sudan',
    regions: ['All Regions', 'Khartoum', 'North Darfur', 'South Darfur', 'West Darfur', 'Central Darfur', 'Blue Nile', 'Red Sea', 'Kassala'],
    hasCrisisData: true
  },
  'Ethiopia': {
    center: [40.5, 8.5],
    zoom: 5.5,
    name: 'Ethiopia',
    regions: ['All Regions', 'Somali', 'Oromia', 'Afar', 'Amhara', 'Tigray', 'SNNPR'],
    hasCrisisData: true
  },
  'South Sudan': {
    center: [31.0, 7.0],
    zoom: 5.5,
    name: 'South Sudan',
    regions: ['All Regions', 'Central Equatoria', 'Jonglei', 'Unity', 'Upper Nile', 'Lakes'],
    hasCrisisData: false
  },
  'Somalia': {
    center: [46.0, 5.0],
    zoom: 5,
    name: 'Somalia',
    regions: ['All Regions', 'Banadir', 'Bay', 'Gedo', 'Lower Juba', 'Middle Shabelle'],
    hasCrisisData: false
  },
  'Yemen': {
    center: [48.0, 15.5],
    zoom: 5.5,
    name: 'Yemen',
    regions: ['All Regions', "Sana'a", 'Aden', 'Taiz', 'Hodeidah', 'Marib'],
    hasCrisisData: false
  }
};

// Generate country config for all NRC operating locations
const generateNRCCountryConfig = (): Record<string, { center: [number, number]; zoom: number; regions: string[]; name: string; hasCrisisData: boolean }> => {
  const config: Record<string, { center: [number, number]; zoom: number; regions: string[]; name: string; hasCrisisData: boolean }> = { ...priorityCountryConfig };

  getOperationalLocations().forEach((location) => {
    if (!config[location.country]) {
      config[location.country] = {
        center: [location.longitude, location.latitude],
        zoom: 5.5,
        name: location.country,
        regions: ['All Regions'],
        hasCrisisData: false
      };
    }
  });

  return config;
};

const countryConfig = generateNRCCountryConfig();

// Group countries by region for dropdown
const getCountriesByNRCRegion = (): Record<NRCRegion, string[]> => {
  const grouped: Record<string, string[]> = {};
  getOperationalLocations().forEach((location) => {
    if (!grouped[location.region]) {
      grouped[location.region] = [];
    }
    if (!grouped[location.region].includes(location.country)) {
      grouped[location.region].push(location.country);
    }
  });
  return grouped as Record<NRCRegion, string[]>;
};

const countriesByRegion = getCountriesByNRCRegion();
const countries = Object.keys(countryConfig).sort();

// Sudan crisis data
const sudanCrisisData: CrisisData[] = [
  { id: 'conflict-1', name: 'Active Conflict - Khartoum', coordinates: [32.53, 15.50], severity: 'critical', trend: 'Ongoing', region: 'Khartoum', affectedPopulation: 2500000 },
  { id: 'conflict-2', name: 'Armed Clashes - El Fasher', coordinates: [25.35, 13.63], severity: 'critical', trend: 'Escalating', region: 'North Darfur', affectedPopulation: 450000 },
  { id: 'displacement-1', name: 'IDP Camp - Zalingei', coordinates: [23.47, 12.91], severity: 'critical', trend: '180k displaced', region: 'Central Darfur', affectedPopulation: 180000 },
  { id: 'food-1', name: 'Famine Risk - West Darfur', coordinates: [22.45, 13.45], severity: 'critical', trend: 'IPC Phase 5', region: 'West Darfur', affectedPopulation: 890000 },
  { id: 'cholera-1', name: 'Cholera Outbreak - Port Sudan', coordinates: [37.22, 19.62], severity: 'critical', cases: 1847, trend: '+89 in 24h', region: 'Red Sea' },
  { id: 'flooding-1', name: 'Flood Risk - White Nile', coordinates: [32.50, 13.16], severity: 'medium', trend: 'Seasonal', region: 'White Nile' },
  { id: 'team-1', name: 'UNHCR Emergency Team', coordinates: [36.20, 15.30], severity: 'response', status: 'Active', members: 12, region: 'Kassala' },
  { id: 'team-2', name: 'WFP Distribution Team', coordinates: [23.00, 13.20], severity: 'response', status: 'Active', members: 8, region: 'West Darfur' },
];

// Ethiopia crisis data
const ethiopiaCrisisData: CrisisData[] = [
  { id: 'cholera', name: 'Cholera Outbreak', coordinates: [42.79, 9.35], severity: 'critical', cases: 247, trend: '+34 in 24h', region: 'Somali' },
  { id: 'cholera-2', name: 'Cholera Spread', coordinates: [44.2, 6.73], severity: 'critical', cases: 89, trend: '+12 in 24h', region: 'Somali' },
  { id: 'flooding', name: 'Flood Risk', coordinates: [39.76, 7.0], severity: 'high', trend: '36h warning', region: 'Oromia' },
  { id: 'drought', name: 'Drought Zone', coordinates: [40.0, 11.5], severity: 'medium', trend: 'Monitoring', region: 'Afar' },
  { id: 'team-1', name: 'WASH Team Alpha', coordinates: [42.5, 9.2], severity: 'response', status: 'Active', members: 6 },
  { id: 'team-2', name: 'Health Team Bravo', coordinates: [44.0, 6.5], severity: 'response', status: 'Active', members: 5 },
];

const getCrisisDataForCountry = (country: string): CrisisData[] => {
  switch (country) {
    case 'Sudan': return sudanCrisisData;
    case 'Ethiopia': return ethiopiaCrisisData;
    default: return sudanCrisisData;
  }
};

const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];

// Fallback token for wireframe demos (same as HTML wireframes)
const MAPBOX_FALLBACK_TOKEN = 'pk.eyJ1Ijoic2FtZnJvbnMiLCJhIjoiY20wcXprNHFmMDM5dDJpb3A3czEyYXU2OCJ9.z7lv5-q6CZW8Xq59kn1bYA';

export function WireframeOverview() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'overview' }, '*');
    }
  }, [isEmbedded]);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const nrcMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const [activeView, setActiveView] = useState<'single' | 'compare' | 'timelapse' | 'nrc-global'>('single');
  const [activeMonth, setActiveMonth] = useState(2); // November
  const [expandedSections, setExpandedSections] = useState<string[]>(['crises', 'analysis']);
  const [selectedCountry, setSelectedCountry] = useState('Sudan');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [showNRCMarkers, setShowNRCMarkers] = useState(false);
  const [expandedNRCRegion, setExpandedNRCRegion] = useState<string | null>(null);
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || MAPBOX_FALLBACK_TOKEN;

  const currentCrisisData = getCrisisDataForCountry(selectedCountry);
  const currentCountryConfig = countryConfig[selectedCountry];
  const currentNRCLocation = getOperationalLocations().find(loc => loc.country === selectedCountry);
  const hasCrisisData = currentCountryConfig?.hasCrisisData || false;

  // Add NRC location markers to map
  const addNRCMarkersToMap = (map: mapboxgl.Map) => {
    // Clear existing NRC markers
    nrcMarkersRef.current.forEach(marker => marker.remove());
    nrcMarkersRef.current = [];

    getOperationalLocations().forEach((location) => {
      const regionColor = regionColors[location.region] || '#718096';

      const el = document.createElement('div');
      el.style.cssText = `
        width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
        font-size: 10px; font-weight: 700; color: white; border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25); cursor: pointer; background: ${regionColor};
        border-radius: 50%;
      `;
      el.innerHTML = '●';

      // Add click handler to update sidebar
      el.addEventListener('click', () => {
        setSelectedCountry(location.country);
        setSelectedRegion('All Regions');
      });

      const operationsList = location.operationTypes.length > 0
        ? location.operationTypes.slice(0, 4).map(t => t.replace('Legal Assistance (ICLA)', 'ICLA')).join(', ')
        : 'Operations active';

      const popup = new mapboxgl.Popup({ offset: 20 }).setHTML(`
        <div style="padding: 8px; font-family: Inter, sans-serif; min-width: 180px;">
          <div style="font-weight: 700; font-size: 14px; margin-bottom: 4px; color: #171717;">${location.country}</div>
          <div style="font-size: 11px; color: ${regionColor}; font-weight: 600; margin-bottom: 8px;">${location.region}</div>
          ${location.description ? `<p style="font-size: 11px; color: #525252; margin-bottom: 8px; line-height: 1.4;">${location.description}</p>` : ''}
          <div style="font-size: 10px; color: #737373; border-top: 1px solid #E5E5E5; padding-top: 8px;">
            <strong style="color: #525252;">Core Activities:</strong><br/>
            ${operationsList}
          </div>
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.longitude, location.latitude])
        .setPopup(popup)
        .addTo(map);

      nrcMarkersRef.current.push(marker);
    });
  };

  // Remove NRC markers from map
  const removeNRCMarkers = () => {
    nrcMarkersRef.current.forEach(marker => marker.remove());
    nrcMarkersRef.current = [];
  };

  // Toggle NRC global view
  const toggleNRCGlobalView = (show: boolean) => {
    setShowNRCMarkers(show);
    if (mapRef.current) {
      if (show) {
        // Clear crisis markers and show NRC markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        addNRCMarkersToMap(mapRef.current);
        // Zoom out to show global view
        mapRef.current.flyTo({
          center: [20, 15],
          zoom: 2,
          duration: 1500
        });
      } else {
        // Remove NRC markers and restore crisis markers
        removeNRCMarkers();
        addMarkersToMap(mapRef.current, getCrisisDataForCountry(selectedCountry));
        const config = countryConfig[selectedCountry];
        if (config) {
          mapRef.current.flyTo({
            center: config.center,
            zoom: config.zoom,
            duration: 1500
          });
        }
      }
    }
  };

  // Handle view change
  const handleViewChange = (view: 'single' | 'compare' | 'timelapse' | 'nrc-global') => {
    setActiveView(view);
    if (view === 'nrc-global') {
      toggleNRCGlobalView(true);
    } else if (showNRCMarkers) {
      toggleNRCGlobalView(false);
    }
  };

  // Add markers to map
  const addMarkersToMap = (map: mapboxgl.Map, crisisData: CrisisData[]) => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const colors: Record<string, string> = {
      critical: '#DC2626',
      high: '#F59E0B',
      medium: '#D97706',
      response: '#059669',
    };

    crisisData.forEach((crisis) => {
      const el = document.createElement('div');
      el.style.cssText = `
        width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700; color: white; border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3); cursor: pointer; background: ${colors[crisis.severity]};
        ${crisis.severity === 'critical' ? 'animation: marker-pulse 1.5s infinite;' : ''}
      `;
      el.innerHTML = crisis.severity === 'response' ? '●' : '!';

      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 8px; font-family: Inter, sans-serif;">
          <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${crisis.name}</div>
          ${crisis.cases ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;"><span style="color: #737373;">Cases</span><span style="font-weight: 600; color: #DC2626;">${crisis.cases}</span></div>` : ''}
          ${crisis.affectedPopulation ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;"><span style="color: #737373;">Affected</span><span style="font-weight: 600; color: #DC2626;">${(crisis.affectedPopulation / 1000).toFixed(0)}k</span></div>` : ''}
          ${crisis.members ? `<div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;"><span style="color: #737373;">Members</span><span style="font-weight: 600;">${crisis.members}</span></div>` : ''}
          <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #E5E5E5;"><span style="color: #737373;">${crisis.cases ? 'Trend' : 'Status'}</span><span style="font-weight: 600; ${crisis.severity === 'critical' ? 'color: #DC2626;' : ''}">${crisis.trend || crisis.status}</span></div>
          <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;"><span style="color: #737373;">Region</span><span style="font-weight: 600;">${crisis.region || 'Field'}</span></div>
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat(crisis.coordinates)
        .setPopup(popup)
        .addTo(map);

      markersRef.current.push(marker);
    });
  };

  // Handle country change
  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');

    // If switching from NRC global view, reset the view
    if (showNRCMarkers) {
      setShowNRCMarkers(false);
      setActiveView('single');
      removeNRCMarkers();
    }

    const config = countryConfig[country];
    if (mapRef.current && config) {
      mapRef.current.flyTo({
        center: config.center,
        zoom: config.zoom,
        duration: 1500
      });
      // Only add crisis markers for countries with crisis data
      if (config.hasCrisisData) {
        addMarkersToMap(mapRef.current, getCrisisDataForCountry(country));
      } else {
        // Clear markers for countries without crisis data
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
      }
    }
  };

  // Handle region change
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
  };

  // Initialize map
  useEffect(() => {
    if (mapRef.current) {
      return;
    }

    const container = document.getElementById('wireframe-overview-map');
    if (!container) {
      return;
    }

    mapboxgl.accessToken = mapboxToken;

    // Start with Sudan
    const config = countryConfig['Sudan'];
    const map = new mapboxgl.Map({
      container: 'wireframe-overview-map',
      style: 'mapbox://styles/mapbox/light-v11',
      center: config.center,
      zoom: config.zoom,
    });

    mapRef.current = map;

    map.on('load', () => {
      addMarkersToMap(map, sudanCrisisData);
    });

    map.on('error', (e) => {
      console.error('[WireframeOverview] Map error:', e);
    });
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  return (
    <WireframeLayout fullHeight embedded={isEmbedded}>
      {/* Main container - force row layout in embedded mode */}
      <div className={cn(
        "flex flex-1 overflow-hidden",
        isEmbedded ? "flex-row" : "flex-col lg:flex-row"
      )}>
        {/* Map Section - force desktop layout in embedded mode */}
        <div className={cn(
          "relative flex-1",
          isEmbedded ? "h-full" : "min-h-[50vh] lg:min-h-0 lg:h-full"
        )}>
          {/* Map Header - force desktop layout in embedded mode */}
          <div className={cn(
            "absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-white via-white/90 to-transparent flex justify-between items-start pointer-events-none",
            isEmbedded
              ? "px-4 py-3 flex-row gap-2"
              : "px-2 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 flex-col sm:flex-row gap-1.5 sm:gap-2"
          )}>
            <div className={cn(
              "flex flex-wrap pointer-events-auto",
              isEmbedded ? "gap-2" : "gap-1.5 sm:gap-2 lg:gap-3"
            )}>
              <FilterDropdown
                label="Country"
                value={selectedCountry}
                options={countries}
                onChange={handleCountryChange}
                embedded={isEmbedded}
              />
              <FilterDropdown
                label="Region"
                value={selectedRegion}
                options={currentCountryConfig?.regions || ['All Regions']}
                onChange={handleRegionChange}
                embedded={isEmbedded}
              />
              <FilterDropdown
                label="Date"
                value="Nov 2025"
                icon="📅"
                options={[]}
                onChange={() => {}}
                className={isEmbedded ? "" : "hidden sm:block"}
                embedded={isEmbedded}
              />
            </div>
            <div className={cn(
              "flex items-center pointer-events-auto",
              isEmbedded ? "gap-2" : "gap-1.5 sm:gap-2"
            )}>
              {/* View mode toggle - force desktop in embedded */}
              <div className="flex bg-white border border-gray-200">
                {(['single', 'compare', 'timelapse', 'nrc-global'] as const).map((view) => (
                  <button
                    key={view}
                    onClick={() => handleViewChange(view)}
                    className={cn(
                      'font-semibold flex items-center transition-colors border-r border-gray-200 last:border-r-0',
                      isEmbedded ? 'px-2 py-1.5 text-[10px] gap-1' : 'px-2 sm:px-3 py-1.5 sm:py-2 text-[9px] sm:text-[11px] gap-1 sm:gap-1.5',
                      activeView === view
                        ? view === 'nrc-global' ? 'bg-accent-500 text-white' : 'bg-gray-900 text-white'
                        : 'bg-transparent text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    {view === 'single' && (isEmbedded ? '◻ Single' : <><span className="hidden sm:inline">◻ Single</span><span className="sm:hidden">◻</span></>)}
                    {view === 'compare' && (isEmbedded ? '◫ Compare' : <><span className="hidden sm:inline">◫ Compare</span><span className="sm:hidden">◫</span></>)}
                    {view === 'timelapse' && (isEmbedded ? '▶ Time' : <><span className="hidden sm:inline">▶ Time</span><span className="sm:hidden">▶</span></>)}
                    {view === 'nrc-global' && (isEmbedded ? '🌍 NRC' : <><span className="hidden sm:inline">🌍 NRC</span><span className="sm:hidden">🌍</span></>)}
                  </button>
                ))}
              </div>
              {!isEmbedded && <TutorialStartButton tutorialId="overview" variant="icon" />}
            </div>
          </div>

          {/* Map - matching CrisisMapping structure exactly */}
          <div
            ref={mapContainerRef}
            id="wireframe-overview-map"
            data-tutorial="crisis-map"
            className="absolute inset-0"
            style={{ width: '100%', height: '100%' }}
          />

          {/* Map Legend - show in embedded mode, hidden on mobile otherwise */}
          <div className={cn(
            "absolute z-10 bg-white border border-gray-200",
            isEmbedded
              ? "block bottom-16 left-2 p-2 min-w-[100px]"
              : "hidden md:block bottom-20 lg:bottom-24 left-2 sm:left-4 p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]"
          )}>
            <div className={cn(
              "font-bold uppercase tracking-wide text-gray-500 mb-2",
              isEmbedded ? "text-[8px]" : "text-[9px] sm:text-[10px]"
            )}>
              {showNRCMarkers ? 'NRC Regions' : 'Legend'}
            </div>
            {showNRCMarkers ? (
              <div className={cn(isEmbedded ? "space-y-1" : "space-y-1 sm:space-y-1.5")}>
                <NRCLegendItem color={regionColors['East Africa and Yemen']} label="E. Africa & Yemen" />
                <NRCLegendItem color={regionColors['Central and West Africa']} label="Central & W. Africa" />
                <NRCLegendItem color={regionColors['Southern Africa']} label="Southern Africa" />
                <NRCLegendItem color={regionColors['Middle East']} label="Middle East" />
                <NRCLegendItem color={regionColors['Asia']} label="Asia" />
                <NRCLegendItem color={regionColors['Europe']} label="Europe" />
                <NRCLegendItem color={regionColors['Americas']} label="Americas" />
              </div>
            ) : (
              <div className={cn(isEmbedded ? "space-y-1" : "space-y-1 sm:space-y-1.5")}>
                <LegendItem color="bg-red-600" label="Critical" />
                <LegendItem color="bg-amber-500" label="High" />
                <LegendItem color="bg-amber-600" label="Medium" />
                <LegendItem color="bg-green-600" label="Teams" />
              </div>
            )}
          </div>

          {/* Map Controls - show in embedded mode, hidden on mobile otherwise */}
          <div className={cn(
            "absolute z-10 flex-col gap-px",
            isEmbedded
              ? "flex bottom-16 right-2"
              : "hidden md:flex bottom-20 lg:bottom-24 right-2 sm:right-4"
          )}>
            <button
              onClick={() => mapRef.current?.zoomIn()}
              className={cn(
                "bg-white border border-gray-200 flex items-center justify-center text-lg hover:bg-gray-50 active:bg-gray-100",
                isEmbedded ? "w-7 h-7" : "w-8 h-8 sm:w-9 sm:h-9"
              )}
            >
              <Plus className={cn(isEmbedded ? "w-4 h-4" : "w-4 h-4 sm:w-5 sm:h-5")} />
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut()}
              className={cn(
                "bg-white border border-gray-200 flex items-center justify-center text-lg hover:bg-gray-50 active:bg-gray-100",
                isEmbedded ? "w-7 h-7" : "w-8 h-8 sm:w-9 sm:h-9"
              )}
            >
              <Minus className={cn(isEmbedded ? "w-4 h-4" : "w-4 h-4 sm:w-5 sm:h-5")} />
            </button>
          </div>

          {/* Time Slider */}
          <div className={cn(
            "absolute z-10 bg-white border border-gray-200",
            isEmbedded
              ? "bottom-1 left-1 right-1 p-2"
              : "bottom-1 left-1 right-1 sm:bottom-2 sm:left-2 sm:right-2 lg:bottom-4 lg:left-4 lg:right-4 p-1.5 sm:p-2 lg:p-4"
          )}>
            <div className={cn(
              "flex justify-between items-center",
              isEmbedded ? "mb-2" : "mb-1.5 sm:mb-2 lg:mb-3"
            )}>
              <span className={cn(
                "font-bold uppercase tracking-wide text-gray-500",
                isEmbedded ? "text-[9px]" : "text-[8px] sm:text-[10px] lg:text-[11px]"
              )}>
                Timeline
              </span>
              <div className={cn(
                "flex items-center",
                isEmbedded ? "gap-1" : "gap-0.5 sm:gap-1 lg:gap-2"
              )}>
                <button className={cn(
                  "bg-gray-100 flex items-center justify-center hover:bg-gray-200 active:bg-gray-300",
                  isEmbedded ? "w-6 h-6" : "w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7"
                )}>
                  <SkipBack className={cn(isEmbedded ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3")} />
                </button>
                <button className={cn(
                  "bg-accent-500 text-white flex items-center justify-center hover:bg-accent-600 active:bg-accent-700",
                  isEmbedded ? "w-6 h-6" : "w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7"
                )}>
                  <Play className={cn(isEmbedded ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3")} />
                </button>
                <button className={cn(
                  "bg-gray-100 flex items-center justify-center hover:bg-gray-200 active:bg-gray-300",
                  isEmbedded ? "w-6 h-6" : "w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7"
                )}>
                  <SkipForward className={cn(isEmbedded ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3")} />
                </button>
              </div>
            </div>
            <div className={cn(
              "flex justify-between items-center relative",
              isEmbedded ? "h-7" : "h-6 sm:h-8 lg:h-10"
            )}>
              <div className={cn(
                "absolute top-1/2 left-0 right-0 bg-gray-200 -translate-y-1/2",
                isEmbedded ? "h-0.5" : "h-0.5 sm:h-1"
              )} />
              {months.map((month, index) => (
                <TimeMarker
                  key={month}
                  label={month}
                  active={index === activeMonth}
                  hasEvent={index === 1 || index === 2}
                  onClick={() => setActiveMonth(index)}
                  embedded={isEmbedded}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Force desktop layout in embedded mode */}
        <aside className={cn(
          "bg-white flex flex-col overflow-y-auto flex-shrink-0",
          isEmbedded
            ? "w-[280px] h-full border-l border-gray-200"
            : "w-full lg:w-[340px] xl:w-[380px] min-h-[250px] lg:min-h-0 lg:h-full border-t lg:border-t-0 lg:border-l border-gray-200"
        )}>
          {/* Panel Header */}
          <div className={cn(
            "bg-gray-50 border-b border-gray-200",
            isEmbedded ? "px-4 py-3" : "px-4 sm:px-6 py-4 sm:py-6"
          )}>
            <div className="flex justify-between items-start mb-1">
              <h1 className={cn(
                "font-bold tracking-tight",
                isEmbedded ? "text-lg" : "text-xl sm:text-2xl"
              )}>{selectedCountry}</h1>
              {hasCrisisData ? (
                <span className={cn(
                  "bg-red-600 text-white font-bold uppercase",
                  isEmbedded ? "px-2 py-0.5 text-[9px]" : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-[10px]"
                )}>
                  {currentCrisisData.filter(c => c.severity === 'critical').length + currentCrisisData.filter(c => c.severity === 'high').length} Active
                </span>
              ) : currentNRCLocation ? (
                <span
                  className={cn(
                    "text-white font-bold uppercase",
                    isEmbedded ? "px-2 py-0.5 text-[9px]" : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-[10px]"
                  )}
                  style={{ backgroundColor: regionColors[currentNRCLocation.region] || '#718096' }}
                >
                  NRC Ops
                </span>
              ) : null}
            </div>
            <div className={cn(
              "text-accent-500",
              isEmbedded ? "text-[11px]" : "text-[11px] sm:text-[13px]"
            )}>
              {hasCrisisData ? (
                <>Humanitarian <span className="text-gray-500">| Jan 2026</span></>
              ) : currentNRCLocation ? (
                <>{currentNRCLocation.region.split(' ')[0]} <span className="text-gray-500">| NRC Office</span></>
              ) : (
                <>Overview <span className="text-gray-500">| Jan 2026</span></>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className={cn(
            "grid grid-cols-3 bg-gray-50 border-b border-gray-200",
            isEmbedded ? "gap-1 px-4 py-2" : "gap-1.5 sm:gap-2 px-4 sm:px-6 py-3 sm:py-4"
          )}>
            {hasCrisisData ? (
              <>
                <QuickStat
                  value={String(currentCrisisData.filter(c => c.severity !== 'response').length)}
                  label="Active Crises"
                  change="+1"
                  up
                />
                <QuickStat
                  value={String(currentCrisisData.filter(c => c.severity === 'response').length)}
                  label="Teams Deployed"
                  change={selectedCountry === 'Sudan' ? '+3' : '+2'}
                  up
                />
                <QuickStat
                  value={selectedCountry === 'Sudan' ? '4.5M' : '45K'}
                  label="At Risk"
                  change={selectedCountry === 'Sudan' ? '+28%' : '+12%'}
                  up
                />
              </>
            ) : currentNRCLocation ? (
              <>
                <QuickStat
                  value={String(currentNRCLocation.operationTypes.length)}
                  label="Core Activities"
                />
                <QuickStat
                  value={currentNRCLocation.yearEstablished ? String(currentNRCLocation.yearEstablished) : '—'}
                  label="Est. Year"
                />
                <QuickStat
                  value="Active"
                  label="Status"
                />
              </>
            ) : (
              <>
                <QuickStat value="—" label="Active Crises" />
                <QuickStat value="—" label="Teams" />
                <QuickStat value="—" label="At Risk" />
              </>
            )}
          </div>

          {/* EWAS Data Pipeline Status */}
          {selectedCountry === 'Sudan' && (
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-accent-50 to-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-accent-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-700">EWAS Sudan Pipeline</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 animate-pulse" />
                  <span className="text-[10px] font-semibold text-green-600">CONNECTED</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="text-center p-1.5 bg-white border border-gray-100">
                  <div className="text-sm font-bold text-accent-500">{mockEWASStatistics.sourcesOnline}</div>
                  <div className="text-[8px] text-gray-500 uppercase">Sources</div>
                </div>
                <div className="text-center p-1.5 bg-white border border-gray-100">
                  <div className="text-sm font-bold text-accent-500">55.4k</div>
                  <div className="text-[8px] text-gray-500 uppercase">Records</div>
                </div>
                <div className="text-center p-1.5 bg-white border border-gray-100">
                  <div className="text-sm font-bold text-red-600">{mockEWASStatistics.criticalAlerts}</div>
                  <div className="text-[8px] text-gray-500 uppercase">Critical</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span className="flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  Last sync: 5 min ago
                </span>
                <Link to="/wireframes/detection" className="text-accent-500 font-medium hover:text-accent-600">
                  View sources →
                </Link>
              </div>
            </div>
          )}

          {/* Active Crises / NRC Operations Section */}
          <div data-tutorial="active-crises">
          {hasCrisisData ? (
            <CollapsibleSection
              title={`Active Crises (${currentCrisisData.filter(c => c.severity !== 'response').length})`}
              expanded={expandedSections.includes('crises')}
              onToggle={() => toggleSection('crises')}
            >
              {currentCrisisData.filter(c => c.severity !== 'response').slice(0, 4).map((crisis, idx) => (
                <Link
                  key={crisis.id}
                  to="/wireframes/detection"
                  className={cn("block px-3 py-3 bg-gray-50 hover:bg-gray-100", idx < 3 && "mb-2")}
                >
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "w-2.5 h-2.5",
                      crisis.severity === 'critical' ? 'bg-red-600 animate-pulse' :
                      crisis.severity === 'high' ? 'bg-amber-500' : 'bg-amber-600'
                    )} />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold">{crisis.name}</div>
                      <div className="text-[11px] text-gray-500">
                        {crisis.region} • {crisis.cases ? `${crisis.cases} cases` : crisis.affectedPopulation ? `${(crisis.affectedPopulation / 1000).toFixed(0)}k affected` : crisis.trend}
                      </div>
                    </div>
                    <CrisisBadge severity={crisis.severity} />
                  </div>
                </Link>
              ))}
            </CollapsibleSection>
          ) : currentNRCLocation ? (
            <CollapsibleSection
              title={`NRC Core Activities (${currentNRCLocation.operationTypes.length})`}
              expanded={expandedSections.includes('crises')}
              onToggle={() => toggleSection('crises')}
            >
              {currentNRCLocation.operationTypes.map((activity, idx) => (
                <div
                  key={activity}
                  className={cn("px-3 py-3 bg-gray-50", idx < currentNRCLocation.operationTypes.length - 1 && "mb-2")}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: regionColors[currentNRCLocation.region] || '#718096' }}
                    />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold">{activity}</div>
                      <div className="text-[11px] text-gray-500">
                        {activity === 'Education' && 'Ensuring displaced children access quality education'}
                        {activity === 'Food Security' && 'Addressing hunger and food insecurity'}
                        {activity === 'Legal Assistance (ICLA)' && 'Helping obtain documentation and exercise rights'}
                        {activity === 'Shelter and Settlements' && 'Providing safe and dignified housing'}
                        {activity === 'WASH' && 'Clean water and sanitation facilities'}
                        {activity === 'Camp Management' && 'Coordinating services in displacement sites'}
                        {activity === 'Protection' && 'Protecting vulnerable populations from harm'}
                        {activity === 'Livelihoods' && 'Supporting income generation and self-reliance'}
                        {activity === 'Cash Assistance' && 'Direct cash transfers to affected families'}
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 font-semibold">Active</span>
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          ) : null}
          </div>

          {/* AI Analysis / Location Overview Section */}
          <div data-tutorial="priority-alerts">
          <CollapsibleSection
            title={hasCrisisData ? "AI Situation Analysis" : "Location Overview"}
            expanded={expandedSections.includes('analysis')}
            onToggle={() => toggleSection('analysis')}
          >
            {hasCrisisData ? (
              <>
                <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 text-[9px] font-bold uppercase tracking-wide mb-2">
                  ✦ AI Analysis
                </div>
                {selectedCountry === 'Sudan' ? (
                  <>
                    <p className="text-[13px] text-gray-600 leading-relaxed">
                      <span className="bg-red-100 px-1 font-medium">
                        Armed conflict in Khartoum has displaced 2.5M people
                      </span>{' '}
                      with urgent humanitarian needs across multiple sectors. Fighting has
                      spread to Darfur regions with{' '}
                      <span className="bg-amber-100 px-1 font-medium">
                        famine conditions (IPC Phase 5)
                      </span>{' '}
                      in West Darfur affecting 890,000 people.
                    </p>
                    <p className="text-[13px] text-gray-600 leading-relaxed mt-4">
                      Cholera outbreak in Port Sudan requires immediate WASH intervention.
                      1,847 confirmed cases with limited medical supplies reaching affected areas.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[13px] text-gray-600 leading-relaxed">
                      <span className="bg-amber-100 px-1 font-medium">
                        Cholera outbreak requires immediate action
                      </span>{' '}
                      with 46-hour intervention window remaining. Current spread pattern
                      indicates waterborne transmission from contaminated wells in Jijiga.
                      Response teams deployed but{' '}
                      <span className="bg-amber-100 px-1 font-medium">
                        IV fluid supplies critically low
                      </span>{' '}
                      at 32% capacity.
                    </p>
                    <p className="text-[13px] text-gray-600 leading-relaxed mt-4">
                      Flood risk in Oromia elevated due to seasonal rainfall patterns.
                      Pre-positioning recommended for Bale and West Arsi zones.
                    </p>
                  </>
                )}
                <Link
                  to="/wireframes/analysis"
                  className="inline-block mt-4 text-xs text-accent-500 font-medium hover:text-accent-600"
                >
                  View full analysis →
                </Link>
              </>
            ) : currentNRCLocation ? (
              <>
                <div
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-white text-[9px] font-bold uppercase tracking-wide mb-2"
                  style={{ backgroundColor: regionColors[currentNRCLocation.region] || '#718096' }}
                >
                  {currentNRCLocation.region}
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed">
                  {currentNRCLocation.description || `NRC operates in ${currentNRCLocation.country} providing humanitarian assistance to displaced and vulnerable populations.`}
                </p>
                {currentNRCLocation.yearEstablished && (
                  <p className="text-[13px] text-gray-600 leading-relaxed mt-4">
                    <span className="bg-accent-100 px-1 font-medium">
                      NRC established operations in {currentNRCLocation.yearEstablished}
                    </span>{' '}
                    and continues to provide multi-sector humanitarian response including{' '}
                    {currentNRCLocation.operationTypes.slice(0, 3).join(', ').toLowerCase()}.
                  </p>
                )}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Location Details
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[12px]">
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Capital</span>
                      <span className="font-medium">{currentNRCLocation.capital}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Region</span>
                      <span className="font-medium">{currentNRCLocation.region.split(' ')[0]}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Latitude</span>
                      <span className="font-medium">{currentNRCLocation.latitude.toFixed(2)}°</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Longitude</span>
                      <span className="font-medium">{currentNRCLocation.longitude.toFixed(2)}°</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-[13px] text-gray-500">No detailed information available for this location.</p>
            )}
          </CollapsibleSection>
          </div>

          {/* Response Status / NRC Presence Section */}
          <CollapsibleSection
            title={hasCrisisData ? "Response Status" : "NRC Presence"}
            expanded={expandedSections.includes('response')}
            onToggle={() => toggleSection('response')}
          >
            {hasCrisisData ? (
              <>
                {/* Team Deployments */}
                <div className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Active Deployments
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 px-3 bg-green-50 border-l-2 border-green-600">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 animate-pulse" />
                        <div>
                          <div className="text-[13px] font-medium">WASH Team Alpha</div>
                          <div className="text-[11px] text-gray-500">Jijiga • 6 members</div>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 font-semibold">Active</span>
                    </div>
                    <div className="flex items-center justify-between py-2 px-3 bg-green-50 border-l-2 border-green-600">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 animate-pulse" />
                        <div>
                          <div className="text-[13px] font-medium">Health Team Bravo</div>
                          <div className="text-[11px] text-gray-500">Kebridehar • 5 members</div>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 font-semibold">Active</span>
                    </div>
                    <div className="flex items-center justify-between py-2 px-3 bg-amber-50 border-l-2 border-amber-500">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-500" />
                        <div>
                          <div className="text-[13px] font-medium">Assessment Team</div>
                          <div className="text-[11px] text-gray-500">En route to Gode • 4 members</div>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 font-semibold">Transit</span>
                    </div>
                  </div>
                </div>

                {/* Resource Status */}
                <div className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Critical Resources
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">IV Fluids</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200">
                          <div className="h-full bg-red-600" style={{ width: '32%' }} />
                        </div>
                        <span className="text-[11px] font-semibold text-red-600">32%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">ORS Packets</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200">
                          <div className="h-full bg-amber-500" style={{ width: '58%' }} />
                        </div>
                        <span className="text-[11px] font-semibold text-amber-600">58%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">Water Purification</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200">
                          <div className="h-full bg-green-600" style={{ width: '74%' }} />
                        </div>
                        <span className="text-[11px] font-semibold text-green-600">74%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/wireframes/operations"
                  className="inline-block text-xs text-accent-500 font-medium hover:text-accent-600"
                >
                  View Operations Center →
                </Link>
              </>
            ) : currentNRCLocation ? (
              <>
                {/* NRC Country Office Info */}
                <div className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Country Office
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 px-3 bg-accent-50 border-l-2 border-accent-500">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-accent-500" />
                        <div>
                          <div className="text-[13px] font-medium">NRC {currentNRCLocation.country}</div>
                          <div className="text-[11px] text-gray-500">{currentNRCLocation.capital} • Country Office</div>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 font-semibold">Active</span>
                    </div>
                  </div>
                </div>

                {/* Regional Context */}
                <div className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Regional Context
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">NRC Region</span>
                      <span className="text-[12px] font-medium">{currentNRCLocation.region}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">Countries in Region</span>
                      <span className="text-[12px] font-medium">
                        {getOperationalLocations().filter(l => l.region === currentNRCLocation.region).length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-[13px]">Active Programs</span>
                      <span className="text-[12px] font-medium">{currentNRCLocation.operationTypes.length}</span>
                    </div>
                  </div>
                </div>

                {/* Neighboring NRC Countries */}
                <div className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                    Other {currentNRCLocation.region.split(' ')[0]} Countries
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {getOperationalLocations()
                      .filter(l => l.region === currentNRCLocation.region && l.country !== currentNRCLocation.country)
                      .slice(0, 5)
                      .map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => handleCountryChange(loc.country)}
                          className="text-[10px] px-2 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                          {loc.country}
                        </button>
                      ))}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-[13px] text-gray-500">No response data available.</p>
            )}
          </CollapsibleSection>

          {/* NRC Global Operations Section */}
          <CollapsibleSection
            title={`NRC Global Operations (${nrcStatistics.totalCountries})`}
            expanded={expandedSections.includes('nrc-operations')}
            onToggle={() => toggleSection('nrc-operations')}
          >
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center p-2 bg-gray-50">
                <div className="text-lg font-bold text-accent-500">{nrcStatistics.totalCountries}</div>
                <div className="text-[9px] text-gray-500 uppercase">Countries</div>
              </div>
              <div className="text-center p-2 bg-gray-50">
                <div className="text-lg font-bold text-accent-500">{nrcStatistics.regionCount}</div>
                <div className="text-[9px] text-gray-500 uppercase">Regions</div>
              </div>
              <div className="text-center p-2 bg-gray-50">
                <div className="text-lg font-bold text-accent-500">~16.5k</div>
                <div className="text-[9px] text-gray-500 uppercase">Staff</div>
              </div>
            </div>

            {/* View on Map Button */}
            <button
              onClick={() => handleViewChange('nrc-global')}
              className={cn(
                "w-full mb-4 py-2 px-3 text-[11px] font-semibold flex items-center justify-center gap-2 transition-colors border",
                activeView === 'nrc-global'
                  ? "bg-accent-500 text-white border-accent-500"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              )}
            >
              <Globe className="w-4 h-4" />
              {activeView === 'nrc-global' ? 'Viewing Global Map' : 'View All on Map'}
            </button>

            {/* Regions Accordions */}
            <div className="space-y-2">
              {Object.entries(countriesByRegion).map(([region, regionCountries]) => {
                const isExpanded = expandedNRCRegion === region;
                const locations = getOperationalLocations().filter(loc => loc.region === region);

                return (
                  <div key={region} className="border border-gray-200">
                    {/* Region Header */}
                    <button
                      onClick={() => setExpandedNRCRegion(isExpanded ? null : region)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: regionColors[region as NRCRegion] || '#718096' }}
                        />
                        <span className="text-[12px] font-semibold text-gray-800">{region}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                          {regionCountries.length}
                        </span>
                      </div>
                      <ChevronDown className={cn(
                        "w-4 h-4 text-gray-400 transition-transform",
                        isExpanded && "rotate-180"
                      )} />
                    </button>

                    {/* Region Content - Expanded */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {locations.map((location, idx) => (
                          <div
                            key={location.id}
                            className={cn(
                              "px-3 py-2.5 hover:bg-gray-100 cursor-pointer transition-colors",
                              idx < locations.length - 1 && "border-b border-gray-100"
                            )}
                            onClick={() => handleCountryChange(location.country)}
                          >
                            <div className="flex items-start gap-2">
                              <MapPin className="w-3.5 h-3.5 text-accent-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-[12px] font-medium text-gray-900">{location.country}</span>
                                  {selectedCountry === location.country && (
                                    <span className="text-[9px] px-1.5 py-0.5 bg-accent-500 text-white rounded">
                                      Selected
                                    </span>
                                  )}
                                </div>
                                {location.description && (
                                  <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">
                                    {location.description}
                                  </p>
                                )}
                                {/* Operation Types */}
                                {location.operationTypes.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {location.operationTypes.slice(0, 3).map((type) => (
                                      <span
                                        key={type}
                                        className="text-[9px] px-1.5 py-0.5 bg-white border border-gray-200 text-gray-600 rounded"
                                      >
                                        {type.replace('Legal Assistance (ICLA)', 'ICLA')}
                                      </span>
                                    ))}
                                    {location.operationTypes.length > 3 && (
                                      <span className="text-[9px] px-1.5 py-0.5 text-gray-400">
                                        +{location.operationTypes.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Core Competencies */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                Core Competencies
              </div>
              <div className="flex flex-wrap gap-1">
                {['Education', 'Food Security', 'ICLA', 'Shelter', 'WASH', 'Camp Mgmt'].map((comp) => (
                  <span
                    key={comp}
                    className="text-[9px] px-2 py-0.5 bg-accent-50 text-accent-700 border border-accent-200"
                  >
                    {comp}
                  </span>
                ))}
              </div>
            </div>
          </CollapsibleSection>

          {/* Recent Activity Section */}
          <CollapsibleSection
            title="Recent Activity"
            expanded={expandedSections.includes('activity')}
            onToggle={() => toggleSection('activity')}
          >
            <div className="space-y-3">
              <ActivityItem
                time="15 min ago"
                title="Alert acknowledged"
                description="Cholera outbreak alert reviewed by Dr. Melese A."
                type="alert"
              />
              <ActivityItem
                time="42 min ago"
                title="Team check-in"
                description="WASH Team Alpha reported safe arrival in Jijiga"
                type="team"
              />
              <ActivityItem
                time="1h ago"
                title="Resource request"
                description="Emergency IV fluid request submitted for Somali Region"
                type="resource"
              />
              <ActivityItem
                time="2h ago"
                title="Analysis generated"
                description="AI situation analysis updated for cholera outbreak"
                type="analysis"
              />
              <ActivityItem
                time="3h ago"
                title="Distribution complete"
                description="Cash assistance distributed to 1,247 households in Dire Dawa"
                type="cash"
              />
            </div>

            <Link
              to="/wireframes/detection"
              className="inline-block mt-4 text-xs text-accent-500 font-medium hover:text-accent-600"
            >
              View all activity →
            </Link>
          </CollapsibleSection>

          {/* Action Buttons - Compact on mobile */}
          <div data-tutorial="quick-actions" className="flex gap-1.5 sm:gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 mt-auto">
            <WireframeButton
              variant="primary"
              icon={<FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
              className="flex-1 justify-center text-[10px] sm:text-[13px]"
            >
              <span className="hidden sm:inline">Generate</span> Briefing
            </WireframeButton>
            <WireframeButton
              icon={<Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
              className="flex-1 justify-center text-[10px] sm:text-[13px]"
            >
              <span className="hidden sm:inline">Create</span> Alert
            </WireframeButton>
          </div>

          {/* Data Source - Compact on mobile */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 text-[9px] sm:text-[11px] text-gray-500">
            Data:{' '}
            <a href="#" className="text-accent-500 hover:underline">
              MOH PHEM
            </a>
            ,{' '}
            <a href="#" className="text-accent-500 hover:underline">
              WHO
            </a>
            ,{' '}
            <a href="#" className="text-accent-500 hover:underline">
              FEWS
            </a>{' '}
            | AI Analysis
            <div className="mt-1.5 sm:mt-2 text-[8px] sm:text-[9px] text-gray-400">
              © Mapbox | Updated: 30m ago
            </div>
          </div>
        </aside>
      </div>

      {/* Add keyframe animation for markers */}
      <style>{`
        @keyframes marker-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(220, 38, 38, 0); }
        }
      `}</style>
    </WireframeLayout>
  );
}

// Helper Components
function FilterDropdown({
  label,
  value,
  icon,
  options,
  onChange,
  className,
  embedded
}: {
  label: string;
  value: string;
  icon?: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
  embedded?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div
        onClick={() => options.length > 0 && setIsOpen(!isOpen)}
        className={cn(
          "bg-white border border-gray-200 cursor-pointer hover:border-gray-300 active:bg-gray-50",
          embedded
            ? "px-3 py-1.5 min-w-[90px]"
            : "px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 min-w-[80px] sm:min-w-[100px] lg:min-w-[120px]"
        )}
      >
        <div className={cn(
          "text-gray-500 uppercase tracking-wide",
          embedded ? "text-[9px]" : "text-[8px] sm:text-[10px]"
        )}>{label}</div>
        <div className={cn(
          "font-semibold flex items-center justify-between",
          embedded ? "text-[11px] gap-1" : "text-[10px] sm:text-[12px] lg:text-[13px] gap-1 sm:gap-2"
        )}>
          <span className={cn(
            "truncate",
            embedded ? "max-w-[70px]" : "max-w-[60px] sm:max-w-[80px] lg:max-w-none"
          )}>{value}</span>
          <ChevronDown className={cn(
            "text-gray-400 transition-transform flex-shrink-0",
            embedded ? "w-3 h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3",
            isOpen && "rotate-180"
          )} />
        </div>
      </div>

      {isOpen && options.length > 0 && (
        <div className={cn(
          "absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 overflow-y-auto",
          embedded ? "min-w-[140px] max-h-[250px]" : "min-w-[120px] sm:min-w-[160px] max-h-[50vh] sm:max-h-[300px]"
        )}>
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "cursor-pointer hover:bg-gray-50 active:bg-gray-100",
                embedded ? "px-3 py-2 text-xs" : "px-3 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] lg:text-xs">
      <span className={cn('w-2 h-2 sm:w-2.5 sm:h-2.5', color)} />
      <span className="truncate">{label}</span>
    </div>
  );
}

function NRCLegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] lg:text-xs">
      <span
        className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="truncate">{label}</span>
    </div>
  );
}

function TimeMarker({
  label,
  active,
  hasEvent,
  onClick,
  embedded,
}: {
  label: string;
  active?: boolean;
  hasEvent?: boolean;
  onClick?: () => void;
  embedded?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-center cursor-pointer z-10 touch-manipulation"
      onClick={onClick}
    >
      <div
        className={cn(
          embedded
            ? (active ? 'w-3 h-3 mt-2 bg-accent-500' : 'w-2 h-2 mt-3')
            : (active ? 'w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 mt-2 sm:mt-3' : 'w-1.5 h-1.5 sm:w-2 sm:h-2 mt-2.5 sm:mt-4'),
          active ? 'bg-accent-500' : hasEvent ? 'bg-amber-500' : 'bg-gray-200'
        )}
      />
      <span
        className={cn(
          embedded ? 'text-[9px] mt-0.5' : 'text-[7px] sm:text-[9px] mt-0.5 sm:mt-1',
          active ? 'text-gray-900 font-semibold' : 'text-gray-500'
        )}
      >
        {label}
      </span>
    </div>
  );
}

function QuickStat({
  value,
  label,
  change,
  up,
}: {
  value: string;
  label: string;
  change?: string;
  up?: boolean;
}) {
  return (
    <div className="text-center">
      <div
        className={cn(
          'text-lg sm:text-xl font-bold leading-tight',
          up ? 'text-red-600' : 'text-green-600'
        )}
      >
        {value}
      </div>
      <div className="text-[8px] sm:text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
      {change && (
        <div
          className={cn(
            'text-[8px] sm:text-[10px] flex items-center justify-center gap-0.5',
            up ? 'text-red-600' : 'text-green-600'
          )}
        >
          {up ? '↑' : '↓'} {change}
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 hover:bg-gray-50 active:bg-gray-100"
      >
        <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide">{title}</span>
        <span className="w-5 h-5 sm:w-6 sm:h-6 border border-gray-200 flex items-center justify-center text-xs sm:text-sm text-gray-500">
          {expanded ? '−' : '+'}
        </span>
      </button>
      {expanded && children && <div className="px-4 sm:px-6 pb-4 sm:pb-6">{children}</div>}
    </div>
  );
}

function ActivityItem({
  time,
  title,
  description,
  type,
}: {
  time: string;
  title: string;
  description: string;
  type: 'alert' | 'team' | 'resource' | 'analysis' | 'cash';
}) {
  const icons: Record<string, { bg: string; color: string; icon: string }> = {
    alert: { bg: 'bg-red-100', color: 'text-red-600', icon: '⚠' },
    team: { bg: 'bg-green-100', color: 'text-green-600', icon: '👥' },
    resource: { bg: 'bg-amber-100', color: 'text-amber-600', icon: '📦' },
    analysis: { bg: 'bg-purple-100', color: 'text-purple-600', icon: '✦' },
    cash: { bg: 'bg-blue-100', color: 'text-blue-600', icon: '💵' },
  };

  const { bg, color, icon } = icons[type];

  return (
    <div className="flex gap-3">
      <div className={cn('w-8 h-8 flex items-center justify-center flex-shrink-0', bg)}>
        <span className={cn('text-sm', color)}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-medium truncate">{title}</span>
          <span className="text-[10px] text-gray-400 flex-shrink-0">{time}</span>
        </div>
        <p className="text-[11px] text-gray-500 truncate">{description}</p>
      </div>
    </div>
  );
}

export default WireframeOverview;
